<template>
  <modal
    class="custom-list-value-resolution-modal"
    :title="title"
    :description="description"
    :open="open"
    @close="$emit('close')"
  >
    <div class="resolution-body">
      <div class="resolution-summary">
        <div v-if="nameConflict" class="summary-line">
          <span class="summary-badge">Name exists</span>
          <span class="summary-text">Name "{{ sourceName }}" already exists.</span>
        </div>
        <div v-if="nameDeletedConflict" class="summary-line summary-line--deleted">
          <span class="summary-badge deleted">Deleted value exists</span>
          <span class="summary-text">Name "{{ sourceName }}" exists as a deleted value.</span>
        </div>
        <div v-if="abbreviationConflict" class="summary-line">
          <span class="summary-badge">Abbreviation exists</span>
          <span class="summary-text">Abbreviation "{{ sourceAbbreviation }}" already exists.</span>
        </div>
        <div v-if="abbreviationDeletedConflict" class="summary-line summary-line--deleted">
          <span class="summary-badge deleted">Deleted abbreviation exists</span>
          <span class="summary-text">Abbreviation "{{ sourceAbbreviation }}" exists as a deleted abbreviation.</span>
        </div>
        <div
          v-if="!nameConflict && !abbreviationConflict && !nameDeletedConflict && !abbreviationDeletedConflict"
          class="summary-line"
        >
          <span class="summary-badge neutral">Review</span>
          <span class="summary-text">Adjust the values before saving.</span>
        </div>
      </div>

      <div v-if="resolvedShowNameField" class="field-group">
        <label class="field-label">Name</label>
        <input
          v-model="draftName"
          type="text"
          class="field-input"
          :class="{ 'field-input--error': nameError }"
          placeholder="Enter name"
          @input="handleNameInput"
        >
        <p v-if="nameError" class="field-error">
          {{ nameErrorMessage }}
        </p>
      </div>

      <div v-if="resolvedShowAbbreviationField" class="field-group">
        <label class="field-label">Abbreviation</label>
        <input
          v-model="draftAbbreviation"
          type="text"
          class="field-input"
          :class="{ 'field-input--error': abbreviationError }"
          placeholder="Enter abbreviation"
          @input="handleAbbreviationInput"
        >
        <p v-if="abbreviationError" class="field-error">
          {{ abbreviationErrorMessage }}
        </p>
      </div>

      <div class="action-row">
        <button type="button" class="btn btn-black text-xs" @click="$emit('skip')">
          Skip
        </button>
        <button type="button" class="btn text-xs save-btn" :disabled="!canSave" @click="emitSave">
          Save Changes
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import Modal from '../../common/modal.vue'

export default {
  name: 'CustomListValueResolutionModal',
  components: { Modal },
  props: {
    open: { type: Boolean, default: false },
    title: { type: String, default: 'Resolve duplicate value' },
    description: { type: String, default: 'Update the values or skip this item.' },
    sourceName: { type: String, default: '' },
    sourceAbbreviation: { type: String, default: '' },
    nameConflict: { type: Boolean, default: false },
    abbreviationConflict: { type: Boolean, default: false },
    nameDeletedConflict: { type: Boolean, default: false },
    abbreviationDeletedConflict: { type: Boolean, default: false },
    showBothFields: { type: Boolean, default: false },
    showNameField: { default: undefined },
    showAbbreviationField: { default: undefined },
    initialName: { type: String, default: '' },
    initialAbbreviation: { type: String, default: '' },
    nameError: { type: Boolean, default: false },
    abbreviationError: { type: Boolean, default: false },
    nameErrorMessage: { type: String, default: 'Value already exists, try a different one.' },
    abbreviationErrorMessage: { type: String, default: 'Value already exists, try a different one.' }
  },
  data () {
    return {
      draftName: '',
      draftAbbreviation: ''
    }
  },
  computed: {
    resolvedShowNameField () {
      if (this.showBothFields) return true
      if (this.showNameField !== undefined) return Boolean(this.showNameField)
      return Boolean(this.sourceName || this.nameConflict || this.nameDeletedConflict)
    },
    resolvedShowAbbreviationField () {
      if (this.showBothFields) return true
      if (this.showAbbreviationField !== undefined) return Boolean(this.showAbbreviationField)
      return Boolean(this.sourceAbbreviation || this.abbreviationConflict || this.abbreviationDeletedConflict)
    },
    canSave () {
      return Boolean(this.draftName.trim())
    }
  },
  watch: {
    open: {
      immediate: true,
      handler (value) {
        if (!value) return
        this.draftName = this.initialName || ''
        this.draftAbbreviation = this.initialAbbreviation || ''
      }
    },
    initialName (value) {
      if (this.open) this.draftName = value || ''
    },
    initialAbbreviation (value) {
      if (this.open) this.draftAbbreviation = value || ''
    }
  },
  methods: {
    handleNameInput () {
      this.$emit('draft-change', {
        name: this.draftName,
        abbreviation: this.draftAbbreviation
      })
    },
    handleAbbreviationInput () {
      this.$emit('draft-change', {
        name: this.draftName,
        abbreviation: this.draftAbbreviation
      })
    },
    emitSave () {
      this.$emit('save', {
        name: this.draftName.trim(),
        abbreviation: this.draftAbbreviation.trim() || null
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.resolution-body {
  width: 420px;
  max-width: 100%;
}

.resolution-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 14px;
}

.summary-line {
  display: flex;
  align-items: center;
  gap: 8px;

  &--deleted .summary-text {
    color: #5b3a9a;
  }
}

.summary-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: var(--text-xs1);
  font-weight: 600;
  background: #ffe7e7;
  color: #9f1239;

  &.neutral {
    background: #eef3fb;
    color: #4b5563;
  }

  &.deleted {
    background: #efe4ff;
    color: #6d28d9;
  }
}

.summary-text {
  font-size: var(--text-xs);
  color: #4b5563;
}

.field-group {
  margin-bottom: 12px;
}

.field-label {
  display: block;
  margin-bottom: 4px;
  font-size: var(--text-xs);
  color: #4b5563;
}

.field-input {
  width: 100%;
  height: 32px;
  border: 1px solid #d8e0e9;
  border-radius: 4px;
  padding: 0 10px;
  font-size: var(--text-xs);
  color: #202124;
  background: #fff;
}

.field-input:focus {
  outline: none;
  border-color: #d2a248;
  box-shadow: 0 0 0 2px rgba(241, 180, 63, 0.18);
}

.field-input--error {
  border-color: #dc2626;
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.12);
}

.field-error {
  margin-top: 4px;
  font-size: var(--text-xs1);
  color: #dc2626;
}

.action-row {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

.save-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
